// Card store — the project's card list. The backend's recovery model is
// always refetch-and-replace (no granular per-card SSE patch), so this
// store is deliberately thin: hold the array, refetch wholesale on any
// card SSE event or turn completion.
//
// `reconcileCards` preserves object identity for unchanged cards across
// the array swap, so a memoised card row skips re-rendering when its
// body / title / extra didn't move.

import { useCallback, useRef, useState } from 'react'
import { api } from '../api'
import type { Card } from './types'

/** Keep prior object identity for cards whose mtime + updated_at both
 *  match, so a wholesale `setCards` doesn't churn every memoised row.
 *  mtime is server-bumped on every write; updated_at is a belt-and-braces
 *  second guard. */
export function reconcileCards(prev: Card[] | undefined, next: Card[]): Card[] {
  if (!prev || prev.length === 0) return next
  const prevById = new Map<string, Card>()
  for (const c of prev) prevById.set(c.card_id, c)
  return next.map((c) => {
    const old = prevById.get(c.card_id)
    return old && old.mtime === c.mtime && old.updated_at === c.updated_at
      ? old
      : c
  })
}

export interface CardStore {
  cards: Card[]
  /** Raw replace — used to paint a cached snapshot on a project switch. */
  setCards: (cards: Card[]) => void
  /** Optimistically transform the card list — desktop edits (peek save,
   *  gap verdict, status change) apply here for instant feedback, then
   *  the card SSE / turn-finished refetch confirms (and `reconcileCards`
   *  re-settles identity). A referentially-equal result is a no-op. */
  patchCards: (updater: (prev: Card[]) => Card[]) => void
  /** Refetch a project's cards. Applies `reconcileCards`, and — so a stale
   *  cross-project fetch (e.g. a background turn finishing in project A
   *  while the user views B) can't clobber the visible list — commits to
   *  state only when `projectId` is still the active project. Returns the
   *  reconciled list regardless, for the caller to cache. */
  refetch: (projectId: string) => Promise<Card[]>
}

export function useCardStore(activeProjectId: string | null): CardStore {
  const [cards, setCardsState] = useState<Card[]>([])
  const cardsRef = useRef<Card[]>(cards)
  cardsRef.current = cards
  const activeRef = useRef(activeProjectId)
  activeRef.current = activeProjectId

  const setCards = useCallback((next: Card[]) => {
    cardsRef.current = next
    setCardsState(next)
  }, [])

  const patchCards = useCallback(
    (updater: (prev: Card[]) => Card[]) => {
      const next = updater(cardsRef.current)
      if (next === cardsRef.current) return
      cardsRef.current = next
      setCardsState(next)
    },
    [],
  )

  const refetch = useCallback(async (projectId: string): Promise<Card[]> => {
    const fresh = await api.listCards(projectId)
    const reconciled = reconcileCards(cardsRef.current, fresh)
    if (activeRef.current === projectId) {
      cardsRef.current = reconciled
      setCardsState(reconciled)
    }
    return reconciled
  }, [])

  return { cards, setCards, patchCards, refetch }
}
